import { atom } from 'jotai';
import { SWRResponse } from 'swr';
import { jobStatusAtomFamily } from 'packages/omnitag/state/job-status';
import { OmnitagSearchResp } from 'packages/lib/api/omnitag/contract/omnitag-search';
import { OmnitagSearchSizeResp } from 'packages/lib/api/omnitag/contract/omnitag-search';
import { OmnitagFilter } from 'packages/lib/api/omnitag/contract/omnitag-filters';
import { atomWithReset } from 'jotai/utils';

// Aug-11-2025 - Used Gemini 2.5 Agent to make filters dynamic (DynamicFilters, StaticSearchQuery, defaultSearchQuery)
// Aug-20-2025 - Used Gemini 2.5 Agent to add support for 'number' filters
// Sep-11-2025 - Used Gemini 2.5 Agent to revamp filter types to use Zod OmnitagFilter type instead of relying on OMNITAG_FILTERS_CONFIG static data
// Oct-20-2025 - Used Gemini 2.5 Agent to assist with migration from Recoil to Jotai

type SelectFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'select' }
>['paramName'];
type TextFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'text' }
>['paramName'];
type NumberFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'number' }
>['paramName'];
type ToggleFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'toggle' }
>['paramName'];

export type SelectFilters = { [K in SelectFilterParamName]: string[] };
export type TextFilters = { [K in TextFilterParamName]: string };
export type NumberFilters = { [K in NumberFilterParamName]: number | null };
export type ToggleFilters = { [K in ToggleFilterParamName]: boolean };

/** These fiters are hard-coded, rather than defined in backend filters config */
export interface StaticSearchQuery {
  text: string;
  logNames: string[];
  pageNo?: number;
  pageSize?: number;
}

// Combine them into the final SearchQuery type
export type SearchQuery = StaticSearchQuery &
  SelectFilters &
  TextFilters &
  NumberFilters &
  ToggleFilters;

export const searchQueryAtom = atomWithReset<SearchQuery | null>(null);

export const hideSentItemsAtom = atom(false);

export const searchPageNoAtom = atomWithReset(1);
export const searchPageSizeAtom = atomWithReset(48);

/** Unfiltered, unpaginated results */
export const allSearchResultsAtom = atomWithReset<
  SWRResponse<OmnitagSearchResp> | undefined
>(undefined);

export const searchResultSizeRespAtom = atomWithReset<
  SWRResponse<OmnitagSearchSizeResp> | undefined
>(undefined);

/** @returns an SWRResponse for the query's filtered search results, where already-sent items are filtered out,
 * if 'Hide Sent Items' filter is enabled */
export const filteredSearchResultsSelector = atom((get) => {
  const searchResults = get(allSearchResultsAtom);
  const hideSentItems = get(hideSentItemsAtom);

  if (!searchResults || searchResults.isValidating || !searchResults.data)
    return searchResults;

  const allResults = searchResults.data.imageMetadata;
  let filteredResults = allResults;

  if (hideSentItems) {
    filteredResults = allResults.filter((result) => {
      const jobStatus = get(
        jobStatusAtomFamily(result.log_name + result.timestamp.toString())
      );
      return jobStatus === null;
    });
  }

  return { ...searchResults, data: filteredResults };
});

export const requestIDAtom = atom<string | null>(null);
